// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Building2, 
  Phone, 
  Mail, 
  Calendar, 
  ArrowRight, 
  DollarSign,
  Star,
  TrendingUp,
  Users
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface PartnerProspect {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  partnership_type: string;
  pipeline_stage: string;
  partnership_value_potential: number;
  strategic_fit_score: number;
  notes: string;
  created_at: string;
}

interface PartnershipPipelineProps {
  searchTerm: string;
}

const PIPELINE_STAGES = [
  { key: 'prospect', label: 'Prospect', color: 'bg-gray-100 text-gray-800' },
  { key: 'initial_discussion', label: 'Initial Discussion', color: 'bg-blue-100 text-blue-800' },
  { key: 'proposal', label: 'Proposal', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-800' },
  { key: 'agreement', label: 'Agreement', color: 'bg-green-100 text-green-800' },
  { key: 'active', label: 'Active Partnership', color: 'bg-emerald-100 text-emerald-800' }
];

const PARTNERSHIP_TYPES = {
  'technology': { label: 'Technology', icon: Building2, color: 'text-blue-600' },
  'content': { label: 'Content', icon: Users, color: 'text-purple-600' },
  'distribution': { label: 'Distribution', icon: TrendingUp, color: 'text-green-600' },
  'strategic': { label: 'Strategic Alliance', icon: Star, color: 'text-yellow-600' }
};

const PartnershipPipeline: React.FC<PartnershipPipelineProps> = ({ searchTerm }) => {
  const [prospects, setProspects] = useState<PartnerProspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState<string>('all');

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from('partner_prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      console.error('Error fetching partner prospects:', error);
      toast.error('Failed to load partner prospects');
    } finally {
      setLoading(false);
    }
  };

  const updatePipelineStage = async (prospectId: string, newStage: string) => {
    try {
      const { error } = await supabase
        .from('partner_prospects')
        .update({ pipeline_stage: newStage })
        .eq('id', prospectId);

      if (error) throw error;
      
      await fetchProspects();
      toast.success('Pipeline stage updated successfully');
    } catch (error) {
      console.error('Error updating pipeline stage:', error);
      toast.error('Failed to update pipeline stage');
    }
  };

  const filteredProspects = prospects.filter(prospect => {
    const matchesSearch = searchTerm === '' || 
      prospect.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prospect.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prospect.partnership_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStage = selectedStage === 'all' || prospect.pipeline_stage === selectedStage;

    return matchesSearch && matchesStage;
  });

  const getStageConfig = (stage: string) => {
    return PIPELINE_STAGES.find(s => s.key === stage) || PIPELINE_STAGES[0];
  };

  const getTypeConfig = (type: string) => {
    return PARTNERSHIP_TYPES[type as keyof typeof PARTNERSHIP_TYPES] || PARTNERSHIP_TYPES.technology;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stage Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedStage === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedStage('all')}
        >
          All Stages ({prospects.length})
        </Button>
        {PIPELINE_STAGES.map(stage => {
          const count = prospects.filter(p => p.pipeline_stage === stage.key).length;
          return (
            <Button
              key={stage.key}
              variant={selectedStage === stage.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedStage(stage.key)}
            >
              {stage.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Pipeline Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProspects.map((prospect) => {
          const stageConfig = getStageConfig(prospect.pipeline_stage);
          const typeConfig = getTypeConfig(prospect.partnership_type);
          const TypeIcon = typeConfig.icon;

          return (
            <Card key={prospect.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <TypeIcon className={`h-5 w-5 ${typeConfig.color}`} />
                    <div>
                      <CardTitle className="text-lg">{prospect.company_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{prospect.contact_name}</p>
                    </div>
                  </div>
                  {prospect.strategic_fit_score && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium">{prospect.strategic_fit_score}/10</span>
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Pipeline Stage */}
                <div className="flex items-center justify-between">
                  <Badge className={stageConfig.color}>
                    {stageConfig.label}
                  </Badge>
                  <Badge variant="outline" className={typeConfig.color}>
                    {typeConfig.label}
                  </Badge>
                </div>

                {/* Value Potential */}
                {prospect.partnership_value_potential && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">
                      {formatCurrency(prospect.partnership_value_potential)} potential
                    </span>
                  </div>
                )}

                {/* Contact Info */}
                <div className="space-y-2">
                  {prospect.contact_email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{prospect.contact_email}</span>
                    </div>
                  )}
                  {prospect.contact_phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      <span>{prospect.contact_phone}</span>
                    </div>
                  )}
                </div>

                {/* Notes Preview */}
                {prospect.notes && (
                  <div className="text-sm text-muted-foreground line-clamp-2">
                    {prospect.notes}
                  </div>
                )}

                {/* Progress to Next Stage */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Pipeline Progress</span>
                    <span>{PIPELINE_STAGES.findIndex(s => s.key === prospect.pipeline_stage) + 1}/{PIPELINE_STAGES.length}</span>
                  </div>
                  <Progress 
                    value={(PIPELINE_STAGES.findIndex(s => s.key === prospect.pipeline_stage) + 1) / PIPELINE_STAGES.length * 100}
                    className="h-2"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {prospect.pipeline_stage !== 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        const currentIndex = PIPELINE_STAGES.findIndex(s => s.key === prospect.pipeline_stage);
                        if (currentIndex < PIPELINE_STAGES.length - 1) {
                          updatePipelineStage(prospect.id, PIPELINE_STAGES[currentIndex + 1].key);
                        }
                      }}
                    >
                      <ArrowRight className="h-3 w-3 mr-1" />
                      Next Stage
                    </Button>
                  )}
                  <Button variant="ghost" size="sm">
                    <Calendar className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProspects.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No partner prospects found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? 'No prospects match your search criteria.' : 'Start building your partnership pipeline.'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Add Partner Prospect
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnershipPipeline;